import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import ProjectCard from './ProjectCard';
import CreateProject from './CreateProject';
import Breadcrumb from './Breadcrumb';
import MobileHeader from './MobileHeader';
import useIsMobile from '../hooks/useIsMobile';
import './HomePage.css';

interface Project {
  project_id: string;
  name: string;
  description: string;
  created_at: string;
}

const HomePage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects`, {
        headers: {
          'Authorization': token || '',
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
    setLoading(false);
  };

  const handleCreateProject = async (projectData: { name: string; description: string }) => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects`, {
        method: 'POST',
        headers: {
          'Authorization': token || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(projectData)
      });
      
      if (response.ok) {
        const newProject = await response.json();
        setProjects([...projects, newProject]);
        setShowCreateForm(false);
      }
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };

  if (loading) {
    return <div className="loading">Loading projects...</div>;
  }

  return (
    <div className="home-page">
      <MobileHeader 
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
        title={isMobile ? 'LLM Console' : 'LLM Playground'}
      />
      <Breadcrumb items={[{ label: 'Home' }]} />
      <div className="header">
        <h1>{isMobile ? 'LLM Console' : 'LLM Development Console'}</h1>
        <button className="create-btn" onClick={() => setShowCreateForm(true)}>
          {isMobile ? '+ New Project' : 'Create Project'}
        </button>
      </div>
      
      {showCreateForm && (
        <CreateProject
          onSubmit={handleCreateProject}
          onCancel={() => setShowCreateForm(false)}
        />
      )}
      
      <div className="projects-grid">
        {projects.map(project => (
          <ProjectCard key={project.project_id} project={project} />
        ))}
        {projects.length === 0 && (
          <div className="empty-state">
            <p>No projects yet. Create your first LLM project!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;